/**
 * "The four ways to do this" — the honest-comparison band.
 *
 * Renders one row per alternative: the approach on the left, what it is
 * genuinely good at in the middle, what it is genuinely weak at on the right.
 * Serac is one of those rows and gets exactly the same two cells as everyone
 * else — a comparison where the author wins every row reads as marketing to a
 * ServiceNow developer and costs more trust than it buys, so the layout is
 * deliberately built so a reader can scan the "weak at" column top to bottom
 * and find our own entry in it.
 *
 * Copy: every user-visible string comes from the `alternatives` namespace in
 * locales/{en,nl}/marketing.json. Nothing is written here.
 *
 *   REQUIRED   alternatives.eyebrow
 *              alternatives.headline
 *              alternatives.body
 *              alternatives.items[] — array of { name, goodAt, weakAt }
 *
 *   OPTIONAL   alternatives.goodAtLabel / alternatives.weakAtLabel
 *                → column headers (desktop) / per-cell labels (mobile)
 *              alternatives.footnote
 *                → the "checked on <date>" stamp under the table
 *              alternatives.deepLinkLabel / alternatives.deepLinkHref
 *                → link out to the long-form comparison essay
 *              items[].sources — array of { label, href }
 *                → per-row citations (repo, licence file, the file a claim
 *                  is read off). `sourceLabel`/`sourceHref` still work as a
 *                  single-citation shorthand.
 *              items[].self (boolean)
 *                → marks our own row; falls back to matching /serac/i on name
 *
 * The body copy promises the reader that every cell was checked against a
 * public repository. A row that asserts something about a named third party
 * and cites nothing breaks that promise, so cite the file the claim is read
 * off — not the project's front page.
 *
 * Every optional key is read through a defaultValue guard and simply does not
 * render when absent, so the page can never paint a raw key name. The whole
 * section returns null unless `items` resolves to a usable array, which also
 * covers the window before the copy lands.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../serac';
import { useIsMobile } from '../../hooks/useMediaQuery';

interface Citation {
  label: string;
  href: string;
}

interface AlternativeItem {
  name: string;
  goodAt: string;
  weakAt: string;
  sources: Citation[];
  self: boolean;
}

const isText = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/** `sources: [{label, href}]`, plus the older single `sourceLabel`/`sourceHref` pair. */
const readSources = (r: Record<string, unknown>): Citation[] => {
  const list = Array.isArray(r.sources)
    ? r.sources.flatMap((s): Citation[] => {
        if (typeof s !== 'object' || s === null) return [];
        const c = s as Record<string, unknown>;
        return isText(c.label) && isText(c.href) ? [{ label: c.label, href: c.href }] : [];
      })
    : [];
  if (isText(r.sourceLabel) && isText(r.sourceHref)) {
    list.push({ label: r.sourceLabel, href: r.sourceHref });
  }
  return list;
};

/**
 * i18next hands back the raw key string when a key is missing, and locale JSON
 * is hand-edited in two languages, so nothing here trusts the shape. A row is
 * dropped unless all three required fields are present strings.
 */
const readItems = (raw: unknown): AlternativeItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): AlternativeItem[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const r = entry as Record<string, unknown>;
    if (!isText(r.name) || !isText(r.goodAt) || !isText(r.weakAt)) return [];
    return [
      {
        name: r.name,
        goodAt: r.goodAt,
        weakAt: r.weakAt,
        sources: readSources(r),
        self: typeof r.self === 'boolean' ? r.self : /serac/i.test(r.name),
      },
    ];
  });
};

const linkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: '0.04em',
  color: 'var(--ice-400)',
  textDecoration: 'none',
} as const;

/** Source citations and the essay link are the only accent-coloured things in
 *  this band — the accent marks what the visitor can act on, nothing else. */
function SourceLink({ href, label }: { href: string; label: string }) {
  if (/^https?:\/\//i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
        {label}
        <Icon.external s={11} aria-hidden="true" />
      </a>
    );
  }
  return (
    <Link to={href} style={linkStyle}>
      {label}
      <Icon.arrow s={11} aria-hidden="true" />
    </Link>
  );
}

export default function SectionAlternatives() {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();

  const items = readItems(t('alternatives.items', { returnObjects: true }) as unknown);
  if (items.length === 0) return null;

  const goodAtLabel = t('alternatives.goodAtLabel', { defaultValue: '' });
  const weakAtLabel = t('alternatives.weakAtLabel', { defaultValue: '' });
  const footnote = t('alternatives.footnote', { defaultValue: '' });
  const deepLinkLabel = t('alternatives.deepLinkLabel', { defaultValue: '' });
  const deepLinkHref = t('alternatives.deepLinkHref', { defaultValue: '' });

  const hasColumnLabels = isText(goodAtLabel) && isText(weakAtLabel);
  const gridColumns = isMobile ? '1fr' : 'minmax(180px, 0.85fr) 1fr 1fr';
  const rowPadding = isMobile ? '20px 18px' : '22px 24px';

  const columnLabel = (text: string) => (
    <div
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--fg-dim)',
      }}
    >
      {text}
    </div>
  );

  // Good vs weak is carried by a check against an x. Both icons are decorative,
  // so the column word has to reach a screen reader from inside the cell: on
  // desktop the header row is only a visual association, and on mobile there is
  // no header row at all.
  const cell = (text: string, tone: 'good' | 'weak', label: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {hasColumnLabels &&
        (isMobile ? columnLabel(label) : <span className="sr-only">{label}: </span>)}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span
          style={{
            color: tone === 'good' ? 'var(--signal-ok)' : 'var(--fg-dim)',
            flexShrink: 0,
            marginTop: 3,
            lineHeight: 0,
          }}
        >
          {tone === 'good' ? <Icon.check s={13} aria-hidden="true" /> : <Icon.x s={13} aria-hidden="true" />}
        </span>
        <span style={{ fontSize: isMobile ? 14 : 15, lineHeight: 1.55, color: 'var(--fg-muted)' }}>{text}</span>
      </div>
    </div>
  );

  return (
    <section
      id="alternatives"
      style={{
        padding: isMobile ? '56px 16px' : '80px 32px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          {t('alternatives.eyebrow')}
        </div>
        <h2
          className="t-display"
          style={{ fontSize: 'clamp(28px, 5vw, 44px)', margin: '0 0 12px', maxWidth: 760, lineHeight: 1.15 }}
        >
          {t('alternatives.headline')}
        </h2>
        <p
          style={{
            fontSize: isMobile ? 15 : 17,
            color: 'var(--fg-muted)',
            maxWidth: 700,
            lineHeight: 1.55,
            marginTop: 0,
            marginBottom: isMobile ? 28 : 40,
          }}
        >
          {t('alternatives.body')}
        </p>

        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 14,
            overflow: 'hidden',
            background: 'var(--surface)',
          }}
        >
          {!isMobile && hasColumnLabels && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: 24,
                padding: '14px 24px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg)',
              }}
            >
              <div />
              {columnLabel(goodAtLabel)}
              {columnLabel(weakAtLabel)}
            </div>
          )}

          {items.map((item, i) => (
            <article
              key={`${item.name}-${i}`}
              style={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: isMobile ? 14 : 24,
                alignItems: 'start',
                padding: rowPadding,
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                // Our own row is marked by elevation, not by the accent —
                // it is not a recommendation, just a "you are here".
                background: item.self ? 'var(--surface-2)' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: isMobile ? 15 : 16,
                    fontWeight: 500,
                    color: 'var(--fg)',
                    lineHeight: 1.35,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {item.name}
                </h3>
                {item.sources.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                    {item.sources.map((s) => (
                      <SourceLink key={s.href} href={s.href} label={s.label} />
                    ))}
                  </div>
                )}
              </div>
              {cell(item.goodAt, 'good', goodAtLabel)}
              {cell(item.weakAt, 'weak', weakAtLabel)}
            </article>
          ))}
        </div>

        {(isText(footnote) || (isText(deepLinkLabel) && isText(deepLinkHref))) && (
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            {isText(footnote) ? (
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  letterSpacing: '0.04em',
                  color: 'var(--fg-dim)',
                  lineHeight: 1.6,
                }}
              >
                {footnote}
              </p>
            ) : (
              <span />
            )}
            {isText(deepLinkLabel) && isText(deepLinkHref) && (
              <SourceLink href={deepLinkHref} label={deepLinkLabel} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
